import {
  createRootRouteWithContext,
  Outlet,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { initGclidCapture } from '@sc/shared-ui';
import NotFound from '../components/NotFound';
import { CookieConsent } from '../components/CookieConsent';
import { StrictMode, useEffect } from 'react';
import appCss from '../styles.css?url';
import env from '../environment';

const AnalyticsInit = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    initGclidCapture();
  }, []);

  return <>{children}</>;
};

export const Route = createRootRouteWithContext<{
  isAuthenticated: boolean;
  head?: string;
}>()({
  component: () => (
    <html lang="en">
      <head>
        {/*
          Plausible is cookieless and independent of Consent Mode.
          Only load when VITE_PLAUSIBLE_SCRIPT_URL is set (Stark Crawl's own
          site script — never ship another product's analytics id).
        */}
        {env.plausibleScriptUrl && (
          <>
            <script
              async
              src={env.plausibleScriptUrl}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `
              window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};
              plausible.init()
            `,
              }}
            />
          </>
        )}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('consent','default',{
                ad_storage:'denied',
                ad_user_data:'denied',
                ad_personalization:'denied',
                analytics_storage:'denied',
                functionality_storage:'denied',
                personalization_storage:'denied',
                security_storage:'granted',
                wait_for_update:2000
              });
              gtag('set','ads_data_redaction',true);
              try {
                var match = document.cookie.match(/(?:^|; )sc_cookie_consent=([^;]*)/);
                if (match) {
                  var saved = JSON.parse(decodeURIComponent(match[1]));
                  var grant = function(on){ return on ? 'granted' : 'denied'; };
                  gtag('consent','update',{
                    ad_storage: grant(saved.marketing === true),
                    ad_user_data: grant(saved.marketing === true),
                    ad_personalization: grant(saved.marketing === true),
                    analytics_storage: grant(saved.statistics === true),
                    functionality_storage: grant(saved.preferences === true),
                    personalization_storage: grant(saved.preferences === true),
                    security_storage: 'granted'
                  });
                  gtag('set','ads_data_redaction', saved.marketing !== true);
                }
              } catch (e) {}
            `,
          }}
        />
        {/*
          Google Consent Mode (the default above, updated from the
          sc_cookie_consent cookie when the visitor has already chosen)
          governs the tags inside GTM. Loaded here as the inline snippet
          because react-gtm-ts injects it post-hydration (too late).
          Only when VITE_GTM_ID is set.
        */}
        {env.gtm && (
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${env.gtm}');`,
            }}
          />
        )}
        <HeadContent />
      </head>
      <body className="bg-background-primary text-white">
        <StrictMode>
          <AnalyticsInit>
            <Outlet />
            <CookieConsent />
          </AnalyticsInit>
        </StrictMode>
        <Scripts />
      </body>
    </html>
  ),
  ssr: true,
  notFoundComponent: () => <NotFound to="/" />,
  head: () => ({
    meta: [
      {
        title: 'Stark Crawl - Web crawling & structured data for market research',
      },
      {
        charSet: 'UTF-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
    ],
    links: [
      { rel: 'icon', href: '/favicon.png' },
      {
        rel: 'icon',
        href: '/favicon.png',
        type: 'image/x-icon',
        sizes: '32x32',
      },
      { rel: 'apple-touch-icon', href: '/favicon.png' },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/open-sans-latin.woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/darker-grotesque-latin.woff2',
        crossOrigin: 'anonymous',
      },
      { rel: 'stylesheet', href: appCss },
    ],
    scripts: [
      ...(!import.meta.env.PROD
        ? [
            {
              type: 'module',
              children: `import RefreshRuntime from "/@react-refresh"
  RefreshRuntime.injectIntoGlobalHook(window)
  window.$RefreshReg$ = () => {}
  window.$RefreshSig$ = () => (type) => type
  window.__vite_plugin_react_preamble_installed__ = true`,
            }
          ]
        : []),
      {
        type: 'module',
        src: import.meta.env.PROD ? '/static/client.js' : '/src/client.tsx',
      },
    ],
  }),
});
